import { HashSet, HasEquals, Vector } from 'prelude-ts';
import Unique from './Unique';
import Formula from './formula/Formula';
import { parse } from './StatementParser';

/**
 * A statement of the form "from `resources` we can derive `requirements`."
 */
class Statement extends Unique implements HasEquals {
  /**
   * Available formulas.
   */
  readonly resources: Vector<Formula>;

  /**
   * Formulas to be derived.
   */
  readonly requirements: Vector<Formula>;

  /**
   * All top level ids in `resources`; used for efficient lookup.
   */
  private readonly resourceSet: HashSet<Formula['id']>;

  /**
   * All top level ids in `requirements`; used for efficient lookup.
   */
  private readonly requirementSet: HashSet<Formula['id']>;

  /**
   * Like `resourceSet`, but contains subformulae instead of top level ones.
   */
  private readonly recursiveResourceSet: HashSet<Formula['id']>;

  /**
   * Like `requirementSet`, but contains subformulae instead of top level ones.
   */
  private readonly recursiveRequirementSet: HashSet<Formula['id']>;

  constructor(
    resources: Vector<Formula>,
    requirements: Vector<Formula>,
    u: Unique | null = null
  ) {
    super(u);
    this.resources = resources;
    this.requirements = requirements;

    this.resourceSet = resources.toSet(f => f.id);
    this.requirementSet = requirements.toSet(f => f.id);

    this.recursiveResourceSet = resources
      .flatMap(f => f.subFormulae())
      .toSet(f => f.id);
    this.recursiveRequirementSet = requirements
      .flatMap(f => f.subFormulae())
      .toSet(f => f.id);
  }

  update(resources: Vector<Formula>, requirements: Vector<Formula>) {
    return new Statement(resources, requirements, this);
  }

  /**
   * True when there are no resources and no requirements.
   */
  isSolved(): boolean {
    return this.resources.isEmpty() && this.requirements.isEmpty();
  }

  /**
   * True if there is a formula with the given id in resources.
   *
   * @param formula formula id to search for
   * @param includeSubFormulae search subformulae in addition to top level ones
   */
  hasResource(formula: Formula['id'], includeSubFormulae: boolean = false) {
    return (
      this.resourceSet.contains(formula) ||
      (includeSubFormulae && this.recursiveResourceSet.contains(formula))
    );
  }

  /**
   * True if there is a formula with the given id in requirements.
   *
   * @param formula formula id to search for
   * @param includeSubFormulae search subformulae in addition to top level ones
   */
  hasRequirement(formula: Formula['id'], includeSubFormulae: boolean = false) {
    return (
      this.requirementSet.contains(formula) ||
      (includeSubFormulae && this.recursiveRequirementSet.contains(formula))
    );
  }

  lookupResource(formula: Formula['id']) {
    return this.resources.find((f: Formula) => f.id === formula);
  }

  lookupRequirement(formula: Formula['id']) {
    return this.requirements.find((f: Formula) => f.id === formula);
  }

  /**
   * True if there is a formula with the given id in resources or requirements.
   * Equivalent to `this.hasResource || this.hasRequirement`.
   *
   * @param formula formula id to search for
   * @param includeSubFormulae search subformulae in addition to top level ones
   */
  hasFormula(formula: Formula['id'], includeSubFormulae: boolean = false) {
    return (
      this.hasResource(formula, includeSubFormulae) ||
      this.hasRequirement(formula, includeSubFormulae)
    );
  }

  /**
   * Parse a statement from string.
   *
   * @param statement string denoting a statement
   */
  static parse(statement: string): Statement {
    return parse(statement);
  }

  /**
   * Check if this statement is syntactically equal to another statement.
   *
   * @param statement the other statement to compare this one to
   */
  equals(that: Statement): boolean {
    return (
      this.resources.equals(that.resources) &&
      this.requirements.equals(that.requirements)
    );
  }

  hashCode(): number {
    return Vector.of(
      this.resources.hashCode(),
      this.requirements.hashCode()
    ).hashCode();
  }
}

export default Statement;
